// src/features/auth/components/ProgressIndicator.tsx
import React from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';

interface ProgressStep {
  key: string;
  title: string;
  description: string;
}

interface ProgressIndicatorProps {
  steps: ProgressStep[];
  currentStep: string;
  onStepClick?: (stepKey: string) => void;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  steps,
  currentStep,
  onStepClick
}) => {
  const currentIndex = steps.findIndex(step => step.key === currentStep);

  const getStepStatus = (index: number) => {
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'current';
    return 'upcoming';
  };

  const getStepClasses = (status: string, clickable: boolean) => {
    const baseClasses = "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors duration-150";
    
    if (status === 'completed') {
      return `${baseClasses} bg-green-500 text-white ${clickable ? 'cursor-pointer hover:bg-green-600' : ''}`;
    } else if (status === 'current') {
      return `${baseClasses} bg-primary-light dark:bg-primary-dark text-white`;
    } else {
      return `${baseClasses} bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400 ${clickable ? 'cursor-pointer hover:bg-neutral-300 dark:hover:bg-neutral-600' : ''}`;
    }
  };

  const getConnectorClasses = (index: number) => {
    const isCompleted = index < currentIndex;
    return `flex-1 h-0.5 ${
      isCompleted 
        ? 'bg-green-500' 
        : 'bg-neutral-200 dark:bg-neutral-700'
    }`;
  };

  const handleStepClick = (stepKey: string, index: number) => {
    // Only allow clicking on completed steps or current step
    if (onStepClick && index <= currentIndex) {
      onStepClick(stepKey);
    }
  };

  return (
    <div className="mb-8">
      {/* Desktop Progress Bar */}
      <div className="hidden sm:block">
        <div className="flex items-center">
          {steps.map((step, index) => {
            const status = getStepStatus(index);
            const isClickable = onStepClick && index <= currentIndex;
            
            return (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => handleStepClick(step.key, index)}
                    disabled={!isClickable}
                    className={getStepClasses(status, !!isClickable)}
                    title={isClickable ? `Go to ${step.title}` : step.title}
                  >
                    {status === 'completed' ? (
                      <CheckIcon className="h-5 w-5" />
                    ) : (
                      index + 1
                    )}
                  </button>
                  <div className="mt-2 text-center">
                    <p className={`text-xs font-medium ${
                      status === 'current' 
                        ? 'text-primary-light dark:text-primary-dark' 
                        : status === 'completed'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-neutral-500 dark:text-neutral-400'
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                      {step.description}
                    </p>
                  </div>
                </div>
                
                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className={getConnectorClasses(index)} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Mobile Progress Bar */}
      <div className="sm:hidden">
        <div className="flex items-center mb-4">
          <div className="flex-1 bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
            <div 
              className="bg-primary-light dark:bg-primary-dark h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / steps.length) * 100}%` }}
            />
          </div>
          <span className="ml-3 text-sm text-neutral-600 dark:text-neutral-400">
            {currentIndex + 1} of {steps.length}
          </span>
        </div>
        
        <div className="text-center">
          <p className="text-sm font-medium text-theme-text dark:text-dark-theme-text">
            {steps[currentIndex]?.title}
          </p>
          <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary mt-1">
            {steps[currentIndex]?.description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProgressIndicator;
